import { PriceLabel } from '@/components/features/shared/utils'
import { AlertWarning } from '@/components/ui/alertWarning'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { BillingFrequency } from '@/domain/offerings/offering'
import { BillingPeriodData, ModifyBillingPeriodDto } from '@/domain/subscriptions/subscriptions'
import { endOfMonth, endOfYear } from 'date-fns'
import { Pencil, X } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Label } from '@/components/ui/label'

interface ModifyBillingPeriodModalProps {
  isOpen: boolean
  onClose: () => void
  billingPeriod: BillingPeriodData | null
  onAdjust: (dto: ModifyBillingPeriodDto) => void
  isPending: boolean
}

const months = [
  { value: '0', label: 'Enero' },
  { value: '1', label: 'Febrero' },
  { value: '2', label: 'Marzo' },
  { value: '3', label: 'Abril' },
  { value: '4', label: 'Mayo' },
  { value: '5', label: 'Junio' },
  { value: '6', label: 'Julio' },
  { value: '7', label: 'Agosto' },
  { value: '8', label: 'Septiembre' },
  { value: '9', label: 'Octubre' },
  { value: '10', label: 'Noviembre' },
  { value: '11', label: 'Diciembre' },
]

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear + 3 - 2000 + 1 }, (_, i) => String(2000 + i))

export const ModifyBillingPeriodModal = ({
  isOpen,
  onClose,
  billingPeriod,
  onAdjust,
  isPending,
}: ModifyBillingPeriodModalProps) => {
  const { control, reset, watch } = useForm({
    defaultValues: {
      totalAmount: '',
      selectedYear: String(currentYear),
      selectedMonth: '0',
      enableMultiPeriod: false,
      endPeriodYear: String(currentYear),
      endPeriodMonth: '0',
    },
  })

  const totalAmount = watch('totalAmount')
  const selectedYear = watch('selectedYear')
  const selectedMonth = watch('selectedMonth')
  const enableMultiPeriod = watch('enableMultiPeriod')
  const endPeriodYear = watch('endPeriodYear')
  const endPeriodMonth = watch('endPeriodMonth')

  const billingFrequency = billingPeriod?.billingFrequency
  const isMonthly = billingFrequency === BillingFrequency.MONTHLY ||
    billingFrequency === BillingFrequency.BIMONTHLY ||
    billingFrequency === BillingFrequency.QUARTERLY ||
    billingFrequency === BillingFrequency.SEMIANNUAL

  const currentTotal = billingPeriod?.totalAmount ?? 0
  const outstandingAmount = billingPeriod?.outstandingAmount ?? 0
  const paidAmount = currentTotal - outstandingAmount
  const minAllowedAmount = paidAmount
  const enteredAmount = parseFloat(totalAmount) || 0
  const difference = enteredAmount - currentTotal

  useEffect(() => {
    if (billingPeriod) {
      const periodStart = new Date(billingPeriod.periodStartDate)
      const periodEnd = new Date(billingPeriod.periodEndDate)
      reset({
        totalAmount: String(billingPeriod.totalAmount),
        selectedYear: String(periodStart.getFullYear()),
        selectedMonth: String(periodStart.getMonth()),
        enableMultiPeriod: false,
        endPeriodYear: String(periodEnd.getFullYear()),
        endPeriodMonth: String(periodEnd.getMonth()),
      })
    }
  }, [billingPeriod, reset])

  const periodStartDate = useMemo(() => {
    if (!selectedYear) return null
    const month = isMonthly ? parseInt(selectedMonth) : 0
    return new Date(parseInt(selectedYear), month, 1)
  }, [selectedYear, selectedMonth, isMonthly])

  const periodEndDate = useMemo(() => {
    if (!periodStartDate) return null
    if (enableMultiPeriod && endPeriodYear) {
      if (isMonthly) {
        return endOfMonth(new Date(parseInt(endPeriodYear), parseInt(endPeriodMonth), 1))
      }
      return endOfYear(new Date(parseInt(endPeriodYear), 0, 1))
    }
    if (billingFrequency === BillingFrequency.ANNUAL) {
      return endOfYear(periodStartDate)
    }
    return endOfMonth(periodStartDate)
  }, [periodStartDate, billingFrequency, enableMultiPeriod, endPeriodYear, endPeriodMonth, isMonthly])

  const handleSubmit = () => {
    if (!billingPeriod) return
    const newTotalAmount = parseFloat(totalAmount)
    if (isNaN(newTotalAmount) || newTotalAmount <= 0) return
    if (!periodStartDate) return
    const dto: ModifyBillingPeriodDto = {
      totalAmount: newTotalAmount,
      periodStartDate,
      billingPeriodId: billingPeriod.id,
    }
    if (enableMultiPeriod && periodEndDate) {
      dto.periodEndDate = periodEndDate
    }
    onAdjust(dto)
  }

  if (!isOpen || !billingPeriod) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Modificar Período</DialogTitle>
          <DialogDescription>
            Modifica el rango de fechas o el precio del período seleccionado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 my-4 flex-grow flex flex-col min-h-0 overflow-y-auto pr-2">
          <div className="bg-muted/30 p-3 rounded-lg border space-y-2 shrink-0">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Monto actual:</span>
              <PriceLabel price={currentTotal} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Monto pagado:</span>
              <PriceLabel price={paidAmount} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Monto pendiente:</span>
              <PriceLabel price={outstandingAmount} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Monto mínimo permitido:</span>
              <PriceLabel price={minAllowedAmount} />
            </div>
          </div>

          <div className={`flex items-center justify-between gap-5`}>
            <div className="shrink-0 ml-1">
              <Label htmlFor='selectedMonth'>{enableMultiPeriod ? 'Período de inicio' : 'Período'} </Label>
              <div className="flex items-center gap-2">
                <div className={isMonthly ? '' : 'hidden'}>
                  <Controller
                    name="selectedMonth"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange} disabled={isPending}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Mes" />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map(m => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <Controller
                  name="selectedYear"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange} disabled={isPending}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue placeholder="Año" />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map(y => (
                          <SelectItem key={y} value={y}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            {enableMultiPeriod && <div className="shrink-0">
              <Label>Último período</Label>
              <div className="flex items-center gap-2">
                <div className={isMonthly ? '' : 'hidden'}>
                  <Controller
                    name="endPeriodMonth"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange} disabled={isPending || !enableMultiPeriod}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Mes" />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map(m => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <Controller
                  name="endPeriodYear"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange} disabled={isPending || !enableMultiPeriod}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue placeholder="Año" />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map(y => (
                          <SelectItem key={y} value={y}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>}
          </div>

          <div className="flex items-center space-x-2">
            <Controller
              name="enableMultiPeriod"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onCheckedChange={onChange}
                  disabled={isPending}
                />
              )}
            />
            <label className="text-sm font-medium">
              Múltiples períodos
            </label>
          </div>

          {enableMultiPeriod && (
            <AlertWarning>Los períodos serán consecutivos desde el primer período hasta el último período inclusive.</AlertWarning>
          )}

          <div className="shrink-0 grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor='totalAmount'>Nuevo monto total</Label>
              <Controller
                name="totalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <input
                    type="number"
                    min={minAllowedAmount}
                    step="0.01"
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    disabled={isPending}
                  />
                )}
              />
            </div>
            <div className="space-y-1">
              <Label>Diferencia</Label>
              <div className={`w-full border rounded-lg px-3 py-2 text-sm text-center font-bold ${difference > 0 ? 'text-destructive' : difference < 0 ? 'text-success' : 'text-foreground'}`}>
                {enteredAmount > 0 ? `${difference > 0 ? '+' : ''}${difference.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' })}` : '—'}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 shrink-0">
          <div className='flex justify-between w-full'>
            <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Cancelar
            </Button>
            <Button
              variant="info"
              onClick={handleSubmit}
              disabled={!enteredAmount || (enteredAmount === currentTotal && !enableMultiPeriod) || isPending || !periodStartDate || (enableMultiPeriod && !periodEndDate)}
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Modificar período
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
